import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { seoLookupApi } from "../api/seo-lookup.api";

export function cityBySlugQueryOptions(slug: string | undefined) {
  return queryOptions({
    queryKey: ["seo", "city", slug ?? null],
    queryFn: async () => {
      if (!slug) return null;
      try {
        return await seoLookupApi.cityBySlug(slug);
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function categoryBySlugQueryOptions(slug: string | undefined) {
  return queryOptions({
    queryKey: ["seo", "category", slug ?? null],
    queryFn: async () => {
      if (!slug) return null;
      try {
        return await seoLookupApi.categoryBySlug(slug);
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * City / category SEO-override lookups, SSR-awaited via `useSuspenseQuery`.
 *
 * These feed the hub editorial blocks (highlights, "About {city}",
 * "What to expect", lede tagline) on category / city / city+category hub
 * pages. They MUST resolve during SSR so that copy lands in the
 * server-rendered HTML and is crawlable without JS — previously they used a
 * plain `useQuery`, so the override was fetched only client-side and the
 * editorial never appeared in the initial HTML (weaker for SEO).
 *
 * `useActivities` on the same page is already a suspense query, so these share
 * its Suspense boundary in ExplorePage — no new fallback needed.
 *
 * Failures and missing/undefined slugs resolve to `null` (the page renders
 * without the override) instead of throwing into an error boundary, preserving
 * the previous graceful-degradation behaviour.
 */
export function useCityBySlug(slug: string | undefined) {
  return useSuspenseQuery(cityBySlugQueryOptions(slug));
}

export function useCategoryBySlug(slug: string | undefined) {
  return useSuspenseQuery(categoryBySlugQueryOptions(slug));
}
